import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Period } from '@spike/model';
import clsx from 'clsx';
import styles from 'components/UI/V3/DatePicker/DatePicker.module.scss';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import Calendar from 'react-calendar';
import { Value } from 'react-calendar/dist/cjs/shared/types';

interface DatePickerBaseProps {
    minDate?: Moment;
    maxDate?: Moment;
    allowedDates?: Array<string>;
    timeZone: string;
    className?: string;
}

interface DatePickerPropsRange extends DatePickerBaseProps {
    rangeMode: true;
    multipleMode?: false;
    value?: Period | null;
    onChange?: (value: Period | null) => void;
}

interface DatePickerPropsSingle extends DatePickerBaseProps {
    rangeMode?: false;
    multipleMode?: false;
    value?: Moment | null;
    onChange?: (value: Moment | null) => void;
}

/** Several independent days: each click adds or removes that day. */
interface DatePickerPropsMultiple extends DatePickerBaseProps {
    rangeMode?: false;
    multipleMode: true;
    value?: Array<Moment>;
    onChange?: (value: Array<Moment>) => void;
}

type DatePickerProps = DatePickerPropsRange | DatePickerPropsSingle | DatePickerPropsMultiple;

const convertFromMomentToDate = (moment: Moment): Date => {
    return new Date(moment.year(), moment.month(), moment.date());
};

export const DatePicker: React.FC<DatePickerProps> = props => {
    const { minDate, maxDate, allowedDates, value, rangeMode, timeZone, className, onChange } = props;
    const multipleMode = 'multipleMode' in props && props.multipleMode === true;
    const allowedDatesSet = React.useMemo(() => {
        return allowedDates && allowedDates.length > 0 ? new Set(allowedDates) : null;
    }, [allowedDates]);

    const selectedDays = React.useMemo(
        () =>
            multipleMode
                ? new Set(((value as Array<Moment> | undefined) ?? []).map(day => day.format('YYYY-MM-DD')))
                : null,
        [multipleMode, value]
    );

    const clickDayHandler = (date: Date) => {
        const day = moment(date).tz(timeZone, true);
        const days = (value as Array<Moment> | undefined) ?? [];

        (onChange as DatePickerPropsMultiple['onChange'])?.(
            selectedDays?.has(day.format('YYYY-MM-DD'))
                ? days.filter(current => !current.isSame(day, 'day'))
                : [...days, day].sort((a, b) => a.valueOf() - b.valueOf())
        );
    };

    // Selected days look like a single selected date.
    const tileClassName = React.useCallback(
        ({ date, view }: { date: Date; view: string }) =>
            view === 'month' && selectedDays?.has(moment(date).tz(timeZone, true).format('YYYY-MM-DD'))
                ? 'react-calendar__tile--active react-calendar__tile--range react-calendar__tile--rangeStart react-calendar__tile--rangeEnd react-calendar__tile--rangeBothEnds'
                : undefined,
        [selectedDays, timeZone]
    );

    const changeHandler = (calendarValue: Value) => {
        if (!calendarValue || calendarValue === null) {
            (onChange as DatePickerPropsSingle['onChange'])?.(null);
            return;
        }

        if (!rangeMode && calendarValue instanceof Date) {
            // Single mode: expect single date
            const singleMoment = moment(calendarValue).tz(timeZone, true);

            return (onChange as DatePickerPropsSingle['onChange'])?.(singleMoment);
        }

        if (rangeMode && Array.isArray(calendarValue)) {
            let endMoment: Moment | null = null;
            const startMoment = moment(calendarValue[0]).tz(timeZone, true);

            if (calendarValue[1] !== null) {
                endMoment = moment(calendarValue[1]).tz(timeZone, true);
            } else {
                endMoment = startMoment.clone();
            }

            (onChange as DatePickerPropsRange['onChange'])?.({ from: startMoment, to: endMoment });
        }
    };

    const calendarValue = React.useMemo(() => {
        if (!value || multipleMode) {
            return null;
        }

        // Single mode: value should be Date
        if (!rangeMode) {
            return convertFromMomentToDate(value as Moment);
        }

        const rangeValue = value as Period;
        const startDateMoment = rangeValue.from;
        const endDateMoment = rangeValue.to;

        const nextRangeValue: [Date, Date] = [
            convertFromMomentToDate(startDateMoment),
            convertFromMomentToDate(endDateMoment)
        ];

        return nextRangeValue;
    }, [value, rangeMode, multipleMode]);

    const tileDisabled = React.useCallback(
        ({ date, view }: { date: Date; view: string }) => {
            if (view !== 'month' || !allowedDatesSet) {
                return false;
            }

            const formattedDate = moment(date).tz(timeZone, true).format('YYYY-MM-DD');

            return !allowedDatesSet.has(formattedDate);
        },
        [allowedDatesSet, timeZone]
    );

    return (
        <div className={styles.root}>
            <Calendar
                minDetail="year"
                calendarType="gregory"
                prev2Label={null}
                next2Label={null}
                allowPartialRange
                minDate={minDate ? convertFromMomentToDate(minDate) : undefined}
                maxDate={maxDate ? convertFromMomentToDate(maxDate) : undefined}
                selectRange={rangeMode}
                showNeighboringMonth={false}
                className={clsx(styles.calendar, className)}
                prevLabel={<FontAwesomeIcon icon={faAngleLeft} />}
                nextLabel={<FontAwesomeIcon icon={faAngleRight} />}
                formatMonth={(locale, date) => date.toLocaleString(locale, { month: 'short' })}
                tileDisabled={tileDisabled}
                value={calendarValue}
                {...(multipleMode ? { tileClassName, onClickDay: clickDayHandler } : { onChange: changeHandler })}
            />
        </div>
    );
};
